import osmtogeojson from 'osmtogeojson';

/**
 * Fetch water bodies (rivers, lakes, canals) for a bounding box.
 *
 * Strategy:
 *  1. Try our own Vercel serverless proxy (/api/overpass) — no CORS issues.
 *  2. If that fails (e.g. local dev without the proxy), fall back to direct
 *     public Overpass endpoints.
 */

const PROXY_URL = '/api/overpass';

const FALLBACK_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://lz4.overpass-api.de/api/interpreter',
  'https://z.overpass-api.de/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
];

function buildQuery(bbox) {
  const { minLat, minLon, maxLat, maxLon } = bbox;
  const bboxStr = `${minLat},${minLon},${maxLat},${maxLon}`;
  return `
    [out:json][timeout:90];
    (
      way["waterway"~"river|canal|stream"](${bboxStr});
      relation["waterway"~"river|canal|stream"](${bboxStr});
      way["natural"="water"](${bboxStr});
      relation["natural"="water"](${bboxStr});
    );
    out body;
    >;
    out skel qt;
  `;
}

async function tryProxy(query) {
  const res = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });
  if (!res.ok) throw new Error(`Proxy HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data;
}

async function tryDirect(endpoint, query) {
  const params = new URLSearchParams();
  params.append('data', query);
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} from ${endpoint}`);
  return res.json();
}

export async function fetchWaterBodies(bbox) {
  const minLat = Number(bbox.minLat);
  const minLon = Number(bbox.minLon);
  const maxLat = Number(bbox.maxLat);
  const maxLon = Number(bbox.maxLon);

  if (isNaN(minLat) || isNaN(minLon) || isNaN(maxLat) || isNaN(maxLon)) {
    throw new Error('Invalid coordinates');
  }

  const query = buildQuery({ minLat, minLon, maxLat, maxLon });
  let osmData = null;
  let lastError = 'All endpoints failed';

  // 1. Try server-side proxy first (deployed on Vercel — no CORS)
  try {
    console.log('[overpass] Trying server-side proxy...');
    osmData = await tryProxy(query);
    console.log('[overpass] Proxy succeeded.');
  } catch (err) {
    console.warn('[overpass] Proxy failed:', err.message, '— falling back to direct.');
    lastError = err.message;
  }

  // 2. Fallback: direct browser requests to public Overpass mirrors
  if (!osmData) {
    for (const endpoint of FALLBACK_ENDPOINTS) {
      try {
        console.log(`[overpass] Trying direct: ${endpoint}`);
        osmData = await tryDirect(endpoint, query);
        console.log(`[overpass] Direct succeeded: ${endpoint}`);
        break;
      } catch (err) {
        console.warn(`[overpass] ${endpoint} failed:`, err.message);
        lastError = err.message;
      }
    }
  }

  if (!osmData || !osmData.elements) {
    throw new Error(`Could not load water bodies. Last error: ${lastError}`);
  }

  const geojson = osmtogeojson(osmData);
  if (!geojson?.features?.length) {
    console.warn('[overpass] No water features found in this bounding box.');
  }
  return geojson;
}

/**
 * Split a LineString feature into segments of size coords each.
 */
export function splitIntoSegments(feature, size = 8) {
  const geom = feature.geometry;
  const coords = (geom.type === 'LineString') ? geom.coordinates : 
                 (geom.type === 'MultiLineString') ? geom.coordinates[0] : null;
  
  const segments = [];
  if (!coords || coords.length < 2) return [];
  
  for (let i = 0; i < coords.length - 1; i += size) {
    const slice = coords.slice(i, i + size + 1);
    if (slice.length < 2) continue;
    segments.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: slice },
      properties: { ...feature.properties },
    });
  }
  return segments;
}

/**
 * Build a center LngLat from a GeoJSON feature for popups.
 */
export function getFeatureCenter(feature) {
  const geom = feature.geometry;
  let coords = [];
  if (geom.type === 'LineString') {
    coords = geom.coordinates;
  } else if (geom.type === 'Polygon') {
    coords = geom.coordinates[0];
  } else if (geom.type === 'MultiPolygon') {
    coords = geom.coordinates[0][0];
  } else if (geom.type === 'Point') {
    coords = [geom.coordinates];
  }
  
  if (!coords || coords.length === 0) return [0, 0];
  const mid = coords[Math.floor(coords.length / 2)];
  return [mid[0], mid[1]];
}
